import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { ChatAttachmentsApi } from '../apis/chat-attachments.api';
import { IKnowledgeDocument } from '../interfaces/knowledge-document.interface';
import { IQuickAction } from '../interfaces/quick-action.interface';
import { ChatHistoryStateService } from './chat-history-state.service';
import { MessageAttachmentsStore } from './message-attachments.store';

export const DRAFT_RETENTION_MS = 30 * 60 * 1000;
export const MAX_RETAINED_DRAFTS = 20;

/** A conversa nova ainda não tem id: o rascunho dela vive nesta chave. */
export const NEW_CONVERSATION_DRAFT = '__new__';

export interface IComposerDraft {
  /** O campo é `contenteditable`: as tags só voltam inteiras pelo HTML. */
  html: string;
  text: string;
  quickAction: IQuickAction | null;
  document: IKnowledgeDocument | null;
  uploads: MessageAttachmentsStore;
  lastUsed: number;
}

/**
 * O que o usuário deixou escrito em cada conversa. O composer é um só na tela,
 * mas texto, habilidade, documento e anexos pertencem à conversa onde foram
 * escritos — trocar de conversa troca o rascunho, não o descarta.
 */
@Injectable({ providedIn: 'root' })
export class ComposerDraftStore implements OnDestroy {
  private drafts = new Map<string, IComposerDraft>();
  private retention: Subscription;

  constructor(
    private chatAttachmentsApi: ChatAttachmentsApi,
    historyState: ChatHistoryStateService
  ) {
    this.retention = timer(60_000, 60_000).subscribe(() => this.prune());
    this.retention.add(
      historyState.resetObservable.subscribe(() => this.clearAll())
    );
  }

  get(sessionId: string): IComposerDraft {
    let draft = this.drafts.get(sessionId);
    if (!draft) {
      draft = {
        html: '',
        text: '',
        quickAction: null,
        document: null,
        uploads: new MessageAttachmentsStore(this.chatAttachmentsApi),
        lastUsed: Date.now()
      };
      this.drafts.set(sessionId, draft);
    }
    draft.lastUsed = Date.now();
    return draft;
  }

  save(
    sessionId: string,
    patch: Partial<Omit<IComposerDraft, 'uploads' | 'lastUsed'>>
  ): void {
    const draft = this.get(sessionId);
    Object.assign(draft, patch);
  }

  clear(sessionId: string): void {
    const draft = this.drafts.get(sessionId);
    if (!draft) {
      return;
    }
    draft.uploads.clear();
    draft.html = '';
    draft.text = '';
    draft.quickAction = null;
    draft.document = null;
  }

  /**
   * O anexo não espera o usuário voltar: sair da conversa revoga o arquivo no
   * servidor. O texto e as tags continuam guardados.
   */
  discardUploads(sessionId: string): void {
    this.drafts.get(sessionId)?.uploads.discard();
  }

  /** A conversa nasceu: o rascunho da tela nova passa a ser o dela. */
  adopt(sessionId: string, from = NEW_CONVERSATION_DRAFT): void {
    const origem = this.drafts.get(from);
    if (!origem || origem === this.drafts.get(sessionId)) {
      return;
    }
    this.drafts.get(sessionId)?.uploads.dispose();
    this.drafts.set(sessionId, { ...origem, lastUsed: Date.now() });
    this.drafts.delete(from);
  }

  remove(sessionId: string): void {
    const draft = this.drafts.get(sessionId);
    if (!draft) {
      return;
    }
    draft.uploads.discard();
    this.drafts.delete(sessionId);
  }

  clearAll(): void {
    this.drafts.forEach((draft) => draft.uploads.dispose());
    this.drafts.clear();
  }

  /** Rascunho antigo prende anexo no servidor: o descarte revoga o que sobrou. */
  prune(now = Date.now()): void {
    const candidatos = [...this.drafts.entries()].sort(
      ([, a], [, b]) => a.lastUsed - b.lastUsed
    );
    for (const [id, draft] of candidatos) {
      if (
        this.drafts.size <= MAX_RETAINED_DRAFTS &&
        now - draft.lastUsed < DRAFT_RETENTION_MS
      ) {
        break;
      }
      draft.uploads.discard();
      this.drafts.delete(id);
    }
  }

  ngOnDestroy(): void {
    this.retention.unsubscribe();
    this.clearAll();
  }
}
